import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Music,
  FolderOpen,
  Trash2,
  Edit2,
  Plus,
  Users,
  ListMusic,
  Crown,
  Tag,
  LayoutDashboard,
  BarChart3,
  Megaphone,
  Activity,
  Sparkles,
} from 'lucide-react';

import {
  fetchCategories,
  fetchTracks,
  fetchArtists,
  uploadTrack,
  deleteTrack,
  createCategory,
  updateCategory,
  deleteCategory,
  fetchAdminStats,
  fetchAdminUsers,
} from '../services/api';

import UploadTrackModal from '../components/UploadTrackModal';
import ArtistManager from '../components/ArtistManager';
import AdminPlaylistManager from '../components/AdminPlaylistManager';
import SectionManager from '../components/SectionManager';
import MoodCategoryManager from '../components/MoodCategoryManager';
import AdManager from '../components/AdManager';
import AdminStoryManager from '../components/AdminStoryManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ✅ Stats en premier
const TABS = [
  { key: 'stats', label: 'Statistiques', icon: BarChart3 },
  { key: 'users', label: 'Utilisateurs', icon: Users },
  { key: 'tracks', label: 'Pistes', icon: Music },
  { key: 'artists', label: 'Artistes', icon: Users },
  { key: 'categories', label: 'Catégories', icon: FolderOpen },
  { key: 'mood-categories', label: 'Cat. Moods', icon: Tag },
  { key: 'playlists', label: 'Playlists', icon: ListMusic },
  { key: 'sections', label: 'Sections', icon: LayoutDashboard },
  { key: 'stories', label: 'Stories', icon: Sparkles },
  { key: 'ads', label: 'Publicités', icon: Megaphone },
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatNumber(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('fr-FR').format(value);
}

function formatShortDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function getPresetRange(preset) {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (preset === '7d') {
    start.setDate(start.getDate() - 6);
  } else if (preset === '90d') {
    start.setDate(start.getDate() - 89);
  } else if (preset === '365d') {
    start.setDate(start.getDate() - 364);
  } else {
    start.setDate(start.getDate() - 29);
  }
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

function buildDailySeries(rawRows = [], start, end) {
  if (!start || !end) return [];
  const map = new Map(
    rawRows.map((row) => {
      const key = String(row.day).slice(0, 10);
      return [key, Number(row.count) || 0];
    })
  );
  const series = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(0, 0, 0, 0);
  while (cursor <= endDate) {
    const key = cursor.toISOString().slice(0, 10);
    series.push({
      label: formatShortDate(cursor),
      value: map.get(key) || 0,
      fullDate: key
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return series;
}

function groupSeries(series, chunkSize) {
  if (!Array.isArray(series) || series.length <= chunkSize) return series;
  const grouped = [];
  for (let i = 0; i < series.length; i += chunkSize) {
    const slice = series.slice(i, i + chunkSize);
    const total = slice.reduce((sum, item) => sum + (item.value || 0), 0);
    const first = slice[0];
    const last = slice[slice.length - 1];
    const label = first && last ? `${first.label} - ${last.label}` : `${i + 1}`;
    grouped.push({ label, value: total });
  }
  return grouped;
}

function HistogramChart({ title, subtitle, data = [], tone = 'emerald' }) {
  const maxValue = Math.max(1, ...data.map((d) => d.value || 0));
  const toneColor = tone === 'purple' ? '#a855f7' : tone === 'pink' ? '#ec4899' : tone === 'blue' ? '#60a5fa' : '#34d399';
  const showValues = data.length <= 14;

  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
        </div>
        <div className="text-xs text-white/60">{data.length ? `${data.length} points` : '—'}</div>
      </div>
      <div className="mt-4 h-44">
        {data.length ? (
          <div className="h-full flex items-end gap-1">
            {data.map((item, index) => {
              const height = Math.max(6, (item.value || 0) / maxValue * 100);
              return (
                <div key={`${item.label}-${index}`} className="flex-1 flex flex-col items-center justify-end gap-1">
                  {showValues && (
                    <span className="text-[10px] text-white/70">{formatNumber(item.value || 0)}</span>
                  )}
                  <div
                    className="w-full rounded-full"
                    style={{
                      height: `${height}%`,
                      background: `linear-gradient(180deg, ${toneColor}, rgba(0,0,0,0.2))`
                    }}
                    title={`${item.label} • ${formatNumber(item.value || 0)}`}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-white/50">
            Pas de données sur cette période.
          </div>
        )}
      </div>
      {data.length ? (
        <div className="mt-2 flex items-center justify-between text-xs text-white/50">
          <span>{data[0]?.label}</span>
          <span>{data[data.length - 1]?.label}</span>
        </div>
      ) : null}
    </div>
  );
}

function PieChart({ title, subtitle, data = [], labelKey, valueKey }) {
  const colors = ['#22c55e', '#a855f7', '#60a5fa', '#ec4899', '#f97316', '#fbbf24', '#14b8a6'];
  const total = data.reduce((sum, row) => sum + (Number(row[valueKey]) || 0), 0);
  if (!data.length || total === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
          </div>
        </div>
        <div className="mt-6 text-sm text-white/50">Pas de données.</div>
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0));
  const maxSlices = 6;
  const slices = sorted.slice(0, maxSlices);
  const rest = sorted.slice(maxSlices);
  if (rest.length) {
    const restValue = rest.reduce((sum, row) => sum + (Number(row[valueKey]) || 0), 0);
    slices.push({ [labelKey]: 'Autres', [valueKey]: restValue });
  }

  let cumulative = 0;
  const paths = slices.map((slice, idx) => {
    const value = Number(slice[valueKey]) || 0;
    const startAngle = (cumulative / total) * 360;
    cumulative += value;
    const endAngle = (cumulative / total) * 360;
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    const r = 46;
    const cx = 60;
    const cy = 60;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const d = [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ');
    return { d, color: colors[idx % colors.length], value, label: slice[labelKey] };
  });

  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
        </div>
        <div className="text-xs text-white/60">{formatNumber(total)} total</div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
        <div className="flex items-center justify-center">
          <svg viewBox="0 0 120 120" className="w-32 h-32">
            {paths.map((slice, idx) => (
              <path key={`${slice.label}-${idx}`} d={slice.d} fill={slice.color} />
            ))}
            <circle cx="60" cy="60" r="28" fill="#0b0b0b" opacity="0.9" />
            <text x="60" y="56" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="600">
              {formatNumber(total)}
            </text>
            <text x="60" y="70" textAnchor="middle" fill="#ffffff88" fontSize="9">
              total
            </text>
          </svg>
        </div>

        <div className="space-y-2">
          {paths.map((slice, idx) => {
            const percent = total ? Math.round((slice.value / total) * 100) : 0;
            return (
              <div key={`${slice.label}-legend-${idx}`} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex w-3 h-3 rounded-full" style={{ background: slice.color }} />
                  <span className="truncate">{slice.label}</span>
                </div>
                <div className="text-white/70">
                  {formatNumber(slice.value)} • {percent}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BarList({ title, subtitle, data = [], labelKey, valueKey, tone = 'emerald' }) {
  const maxValue = Math.max(1, ...data.map((row) => Number(row[valueKey]) || 0));
  const toneClass =
    tone === 'purple'
      ? 'from-purple-400/70 to-purple-600/80'
      : tone === 'blue'
        ? 'from-blue-400/70 to-blue-600/80'
        : tone === 'pink'
          ? 'from-pink-400/70 to-pink-600/80'
          : 'from-emerald-400/70 to-emerald-600/80';

  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
        </div>
        <div className="text-xs text-white/60">{data.length ? `${data.length} entrées` : '—'}</div>
      </div>
      <div className="mt-4 space-y-3">
        {data.length ? (
          data.map((row, index) => {
            const value = Number(row[valueKey]) || 0;
            const width = Math.max(4, (value / maxValue) * 100);
            return (
              <div key={`${row[labelKey]}-${index}`} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{row[labelKey]}</span>
                  <span className="text-white/70">{formatNumber(value)}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${toneClass}`} style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-sm text-white/50">Pas de données.</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, tone, icon: Icon, sub }) {
  const toneMap = {
    purple: {
      ring: 'ring-purple-500/20',
      border: 'border-purple-500/20',
      glow: 'shadow-[0_0_0_1px_rgba(168,85,247,0.12),0_12px_40px_-18px_rgba(168,85,247,0.55)]',
      bg: 'from-purple-600/20 via-purple-900/10 to-black/10',
      chip: 'bg-purple-500/15 text-purple-200 border-purple-500/25',
      icon: 'text-purple-200',
    },
    emerald: {
      ring: 'ring-emerald-500/20',
      border: 'border-emerald-500/20',
      glow: 'shadow-[0_0_0_1px_rgba(52,211,153,0.12),0_12px_40px_-18px_rgba(52,211,153,0.45)]',
      bg: 'from-emerald-600/20 via-emerald-900/10 to-black/10',
      chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/25',
      icon: 'text-emerald-200',
    },
    orange: {
      ring: 'ring-orange-500/20',
      border: 'border-orange-500/20',
      glow: 'shadow-[0_0_0_1px_rgba(249,115,22,0.12),0_12px_40px_-18px_rgba(249,115,22,0.45)]',
      bg: 'from-orange-600/20 via-orange-900/10 to-black/10',
      chip: 'bg-orange-500/15 text-orange-200 border-orange-500/25',
      icon: 'text-orange-200',
    },
    blue: {
      ring: 'ring-blue-500/20',
      border: 'border-blue-500/20',
      glow: 'shadow-[0_0_0_1px_rgba(59,130,246,0.12),0_12px_40px_-18px_rgba(59,130,246,0.45)]',
      bg: 'from-blue-600/20 via-blue-900/10 to-black/10',
      chip: 'bg-blue-500/15 text-blue-200 border-blue-500/25',
      icon: 'text-blue-200',
    },
    pink: {
      ring: 'ring-pink-500/20',
      border: 'border-pink-500/20',
      glow: 'shadow-[0_0_0_1px_rgba(236,72,153,0.12),0_12px_40px_-18px_rgba(236,72,153,0.45)]',
      bg: 'from-pink-600/20 via-pink-900/10 to-black/10',
      chip: 'bg-pink-500/15 text-pink-200 border-pink-500/25',
      icon: 'text-pink-200',
    },
    neutral: {
      ring: 'ring-white/10',
      border: 'border-white/10',
      glow: 'shadow-[0_0_0_1px_rgba(255,255,255,0.08)]',
      bg: 'from-white/10 via-white/5 to-black/10',
      chip: 'bg-white/10 text-white/70 border-white/10',
      icon: 'text-white/80',
    },
  };

  const t = toneMap[tone] || toneMap.neutral;

  return (
    <div
      className={cx(
        'relative overflow-hidden rounded-3xl border bg-gradient-to-br backdrop-blur',
        'p-6 sm:p-7 ring-1',
        t.border,
        t.ring,
        t.bg,
        t.glow
      )}
    >
      <div className="pointer-events-none absolute -top-24 -right-24 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={cx('inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs', t.chip)}>
            <Sparkles size={14} className="opacity-90" />
            <span className="truncate">{title}</span>
          </div>

          <div className="mt-4 flex items-end gap-3">
            <div className="text-4xl sm:text-5xl font-extrabold tracking-tight">
              {value ?? '—'}
            </div>
            {sub ? (
              <div className="pb-1 text-sm text-white/60">{sub}</div>
            ) : null}
          </div>
        </div>

        <div className={cx('shrink-0 w-12 h-12 rounded-2xl border border-white/10 bg-black/20 flex items-center justify-center', t.glow)}>
          {Icon ? <Icon size={22} className={t.icon} /> : <Activity size={22} className={t.icon} />}
        </div>
      </div>

      <div className="mt-6 h-[10px] w-full rounded-full bg-black/25 border border-white/5 overflow-hidden">
        <div className={cx('h-full rounded-full', tone === 'emerald' ? 'bg-emerald-400/70' : '')} style={{ width: '70%' }} />
      </div>
      <div className="mt-2 text-xs text-white/45">Vue d’ensemble • Données administrateur</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading, logout } = useAuth();

  // ✅ par défaut: stats
  const [activeTab, setActiveTab] = useState('stats');
  const [tracks, setTracks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [artists, setArtists] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [statsRange, setStatsRange] = useState(() => {
    const preset = '30d';
    const range = getPresetRange(preset);
    return { preset, start: range.start, end: range.end };
  });

  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);

  // Filtres (tracks)
  const [trackSearchQuery, setTrackSearchQuery] = useState('');
  const [trackCategoryFilter, setTrackCategoryFilter] = useState('');
  const [trackTypeFilter, setTrackTypeFilter] = useState('all'); // all | music | mood

  // Filtres (utilisateurs)
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all'); // all | ADMIN | USER
  const [userPremiumFilter, setUserPremiumFilter] = useState('all'); // all | premium | free

  const statsParams = useMemo(() => {
    if (statsRange.start && statsRange.end) {
      return { start: statsRange.start, end: statsRange.end };
    }
    return { period: statsRange.preset || '30d' };
  }, [statsRange]);

  useEffect(() => {
    if (authLoading) return;
    if (user && isAdmin()) {
      loadData();
    }
    // eslint-disable-next-line
  }, [activeTab, authLoading, user]);

  useEffect(() => {
    if (authLoading) return;
    if (activeTab !== 'stats') return;
    if (user && isAdmin()) {
      loadData();
    }
    // eslint-disable-next-line
  }, [statsParams]);

  const loadData = async () => {
    setLoading(true);
    setStatsError('');
    setUsersError('');
    try {
      const needsCatalog =
        activeTab === 'tracks' ||
        activeTab === 'artists' ||
        activeTab === 'categories' ||
        activeTab === 'mood-categories' ||
        activeTab === 'playlists' ||
        activeTab === 'sections' ||
        activeTab === 'stories' ||
        activeTab === 'ads';

      if (needsCatalog) {
        const [catsData, artistsData] = await Promise.all([fetchCategories(), fetchArtists()]);
        setCategories(catsData);
        setArtists(artistsData);
      }

      if (activeTab === 'tracks') {
        const tracksData = await fetchTracks({ include_moods: true });
        setTracks(tracksData);
      } else if (activeTab === 'users') {
        const usersData = await fetchAdminUsers();
        setUsersList(Array.isArray(usersData) ? usersData : []);
      } else if (activeTab === 'stats') {
        const statsData = await fetchAdminStats(statsParams);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
      if (activeTab === 'stats') {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          setStats(null);
          setStatsError('Session invalide. Merci de vous reconnecter pour voir les statistiques.');
          logout();
        } else {
          setStatsError('Impossible de charger les statistiques pour le moment.');
        }
      } else if (activeTab === 'users') {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          setUsersList([]);
          setUsersError('Session invalide. Merci de vous reconnecter pour voir les utilisateurs.');
          logout();
        } else {
          setUsersList([]);
          setUsersError('Impossible de charger les utilisateurs pour le moment.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTrack = async (formData, onProgress) => {
    try {
      setLoading(true);
      await uploadTrack(formData, onProgress);
      setShowUploadModal(false);
      await loadData();
    } catch (error) {
      alert("Erreur lors de l'upload: " + (error.response?.data?.error || error.message));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrack = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette piste ?')) return;

    try {
      await deleteTrack(id);
      await loadData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      setLoading(true);
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: formData.get('name'),
          description: formData.get('description'),
        });
      } else {
        await createCategory({
          name: formData.get('name'),
          description: formData.get('description'),
        });
      }

      setShowCategoryModal(false);
      setEditingCategory(null);
      e.target.reset();
      await loadData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const statsPeriodLabel = useMemo(() => {
    const start = stats?.period?.start || statsRange.start;
    const end = stats?.period?.end || statsRange.end;
    if (!start || !end) return '';
    return `Du ${formatShortDate(start)} au ${formatShortDate(end)}`;
  }, [stats, statsRange.start, statsRange.end]);

  const statsBackendLabel = useMemo(() => {
    return process.env.REACT_APP_BACKEND_URL || '';
  }, []);
  const isLocalBackend = useMemo(() => {
    return statsBackendLabel.includes('localhost') || statsBackendLabel.includes('127.0.0.1');
  }, [statsBackendLabel]);

  const seriesStart = useMemo(() => {
    return stats?.period?.start || statsRange.start || '';
  }, [stats, statsRange.start]);

  const seriesEnd = useMemo(() => {
    return stats?.period?.end || statsRange.end || '';
  }, [stats, statsRange.end]);

  const streamsSeries = useMemo(() => {
    if (!seriesStart || !seriesEnd) return [];
    return buildDailySeries(stats?.streams?.daily || [], seriesStart, seriesEnd);
  }, [stats, seriesStart, seriesEnd]);

  const adImpressionsSeries = useMemo(() => {
    if (!seriesStart || !seriesEnd) return [];
    return buildDailySeries(stats?.ad_impressions?.daily || [], seriesStart, seriesEnd);
  }, [stats, seriesStart, seriesEnd]);

  const streamsSeriesDisplay = useMemo(() => {
    if (streamsSeries.length > 180) return groupSeries(streamsSeries, 14);
    if (streamsSeries.length > 90) return groupSeries(streamsSeries, 7);
    return streamsSeries;
  }, [streamsSeries]);

  const adImpressionsSeriesDisplay = useMemo(() => {
    if (adImpressionsSeries.length > 180) return groupSeries(adImpressionsSeries, 14);
    if (adImpressionsSeries.length > 90) return groupSeries(adImpressionsSeries, 7);
    return adImpressionsSeries;
  }, [adImpressionsSeries]);

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) return;

    try {
      await deleteCategory(id);
      await loadData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || error.message));
    }
  };

  const getArtistName = (artistId) => {
    const artist = artists.find((a) => a.id === artistId);
    return artist ? artist.name : '';
  };

  const filteredTracks = useMemo(() => {
    const q = trackSearchQuery.trim().toLowerCase();

    return tracks.filter((track) => {
      const artistName = getArtistName(track.artist_id);

      const matchesSearch =
        !q ||
        (track.title || '').toLowerCase().includes(q) ||
        (track.artist || '').toLowerCase().includes(q) ||
        (artistName || '').toLowerCase().includes(q);

      const matchesCategory =
        !trackCategoryFilter || String(track.category_id) === String(trackCategoryFilter);

      const matchesType =
        trackTypeFilter === 'all' ||
        (trackTypeFilter === 'mood' && track.is_mood) ||
        (trackTypeFilter === 'music' && !track.is_mood);

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [tracks, trackSearchQuery, trackCategoryFilter, trackTypeFilter, artists]);

  const filteredUsers = useMemo(() => {
    const q = userSearchQuery.trim().toLowerCase();
    return usersList.filter((u) => {
      const matchesSearch =
        !q ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q);

      const matchesStatus =
        userStatusFilter === 'all' || String(u.status || '').toUpperCase() === userStatusFilter;

      const isPremium = Boolean(u.is_premium) || String(u.subscription_type || '').toLowerCase() === 'premium';
      const matchesPremium =
        userPremiumFilter === 'all' ||
        (userPremiumFilter === 'premium' && isPremium) ||
        (userPremiumFilter === 'free' && !isPremium);

      return matchesSearch && matchesStatus && matchesPremium;
    });
  }, [usersList, userSearchQuery, userStatusFilter, userPremiumFilter]);

  // Guards
  if (authLoading) {
    return (
      <div className="h-screen w-full bg-[#05060a] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/80 mx-auto mb-4" />
          <p className="text-white/70">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return (
      <div className="h-screen w-full bg-[#05060a] text-white flex items-center justify-center px-6">
        <div className="max-w-lg w-full text-center bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Accès refusé</h1>
          <p className="text-white/60 mb-7">
            Vous devez être connecté en tant qu&apos;administrateur pour accéder à cette page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-white text-black rounded-full font-semibold hover:scale-[1.02] transition-transform"
          >
            Retour à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-[#05060a] text-white flex flex-col overflow-hidden relative">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-purple-600/20 blur-3xl" />
        <div className="absolute top-24 -right-40 h-[28rem] w-[28rem] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      {/* Top */}
      <div className="shrink-0 sticky top-0 z-40 w-full">
        <header className="backdrop-blur-xl bg-black/55 border-b border-white/10 w-full">
          <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Administration</h1>
              <p className="text-xs sm:text-sm text-white/60 mt-0.5">
                Bienvenue <span className="text-white/80 font-medium">{user?.name}</span>
              </p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full transition-colors text-sm"
            >
              Retour au site
            </button>
          </div>
        </header>

        <div className="backdrop-blur-xl bg-black/40 border-b border-white/10 w-full">
          <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16">
            <div className="flex gap-2 overflow-x-auto py-3">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={cx(
                    'shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all',
                    activeTab === key
                      ? 'bg-emerald-500/15 border-emerald-400/30 text-white shadow-[0_0_0_1px_rgba(52,211,153,0.15)]'
                      : 'bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                  )}
                >
                  <Icon size={16} className={cx(activeTab === key ? 'text-emerald-300' : 'text-white/60')} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN SCROLL */}
      <main className="flex-1 overflow-y-auto relative w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-6">
        {/* STATS (first tab) */}
        {activeTab === 'stats' && (
          <section className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold">Statistiques</h2>
                <p className="text-sm text-white/60 mt-1">
                  Vue d’ensemble rapide sur l’activité de la plateforme.
                </p>
                {statsBackendLabel && (
                  <p className="text-xs text-white/40 mt-1">
                    Source API : {statsBackendLabel}
                    {!isLocalBackend && (
                      <span className="ml-2 text-red-400">⚠️ Backend non local</span>
                    )}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2">
                  <span className="text-xs text-white/60">Période</span>
                  <select
                    value={statsRange.preset}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'custom') {
                        setStatsRange((prev) => ({ ...prev, preset: value }));
                      } else {
                        const range = getPresetRange(value);
                        setStatsRange({ preset: value, start: range.start, end: range.end });
                      }
                    }}
                    className="bg-transparent text-sm outline-none"
                  >
                    <option value="7d">7 jours</option>
                    <option value="30d">30 jours</option>
                    <option value="90d">90 jours</option>
                    <option value="365d">1 an</option>
                    <option value="custom">Personnalisé</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs text-white/70">
                  <span className="text-[11px] text-white/50">jj/mm/aaaa</span>
                  <input
                    type="date"
                    value={statsRange.start}
                    onChange={(e) => setStatsRange((prev) => ({ ...prev, start: e.target.value, preset: 'custom' }))}
                    className="bg-transparent outline-none"
                  />
                  <span>→</span>
                  <input
                    type="date"
                    value={statsRange.end}
                    onChange={(e) => setStatsRange((prev) => ({ ...prev, end: e.target.value, preset: 'custom' }))}
                    className="bg-transparent outline-none"
                  />
                </div>

                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full transition-colors text-sm"
                >
                  <Activity size={16} />
                  Rafraîchir
                </button>
              </div>
            </div>

            {statsError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-200 text-sm">
                {statsError}
              </div>
            )}

            {!stats ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-white/60">
                {loading ? 'Chargement...' : 'Aucune donnée.'}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                  <StatCard title="Utilisateurs" value={stats.users} tone="purple" icon={Users} sub="Comptes actifs" />
                  <StatCard title="Pistes" value={stats.tracks} tone="emerald" icon={Music} sub="Catalogue total" />
                  <StatCard title="Artistes" value={stats.artists} tone="orange" icon={Users} sub="Référencés" />
                  <StatCard title="Catégories" value={stats.categories} tone="blue" icon={FolderOpen} sub="Univers" />
                  <StatCard title="Playlists" value={stats.playlists} tone="pink" icon={ListMusic} sub="Éditorial" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    title="Streams (période)"
                    value={formatNumber(stats.streams?.total)}
                    tone="emerald"
                    icon={Activity}
                    sub={statsPeriodLabel}
                  />
                  <StatCard
                    title="Impressions pubs"
                    value={formatNumber(stats.ad_impressions?.total)}
                    tone="purple"
                    icon={Megaphone}
                    sub={statsPeriodLabel}
                  />
                  <StatCard
                    title="Connexions"
                    value={formatNumber(stats.logins?.total)}
                    tone="blue"
                    icon={Users}
                    sub={statsPeriodLabel}
                  />
                </div>

 

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <PieChart
                    title="Catégories les plus écoutées"
                    subtitle="Répartition des streams"
                    data={stats.top_categories || []}
                    labelKey="category_name"
                    valueKey="streams"
                  />
                  <BarList
                    title="Musiques les plus écoutées"
                    subtitle="Top titres sur la période"
                    data={(stats.top_tracks || []).map((track) => ({
                      label: `${track.title}${track.artist ? ` • ${track.artist}` : ''}`,
                      streams: track.streams
                    }))}
                    labelKey="label"
                    valueKey="streams"
                    tone="pink"
                  />
                  <PieChart
                    title="Connexions par pays"
                    subtitle="Répartition des connexions"
                    data={stats.logins?.by_country || []}
                    labelKey="country"
                    valueKey="count"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">Résumé</h3>
                        <p className="text-sm text-white/60 mt-1">
                          Quelques repères rapides pour vérifier la santé du contenu.
                        </p>
                      </div>
                      <div className="w-11 h-11 rounded-2xl bg-black/25 border border-white/10 flex items-center justify-center">
                        <BarChart3 size={20} className="text-white/70" />
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="text-xs text-white/60">Moyenne pistes / artiste</div>
                        <div className="mt-2 text-2xl font-bold">
                          {stats.artists ? Math.round((stats.tracks / stats.artists) * 10) / 10 : '—'}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="text-xs text-white/60">Moyenne pistes / playlist</div>
                        <div className="mt-2 text-2xl font-bold">
                          {stats.playlists ? Math.round((stats.tracks / stats.playlists) * 10) / 10 : '—'}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="text-xs text-white/60">Moyenne pistes / catégorie</div>
                        <div className="mt-2 text-2xl font-bold">
                          {stats.categories ? Math.round((stats.tracks / stats.categories) * 10) / 10 : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">Actions rapides</h3>
                        <p className="text-sm text-white/60 mt-1">Gagner du temps.</p>
                      </div>
                      <div className="w-11 h-11 rounded-2xl bg-black/25 border border-white/10 flex items-center justify-center">
                        <Sparkles size={20} className="text-white/70" />
                      </div>
                    </div>

                    <div className="mt-5 grid gap-2">
                      <button
                        onClick={() => {
                          setActiveTab('tracks');
                          setShowUploadModal(true);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-colors"
                      >
                        <Plus size={18} />
                        Ajouter une piste
                      </button>

                      <button
                        onClick={() => setActiveTab('categories')}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition-colors"
                      >
                        <FolderOpen size={18} />
                        Gérer les catégories
                      </button>

                      <button
                        onClick={() => setActiveTab('playlists')}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition-colors"
                      >
                        <ListMusic size={18} />
                        Gérer les playlists
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {/* USERS */}
        {activeTab === 'users' && (
          <section className="h-full flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Liste des Utilisateurs</h2>
                <p className="text-sm text-white/60 mt-1">Consulte, filtre et surveille les comptes.</p>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-6">
                  <label className="block text-xs font-medium text-white/60 mb-2">Recherche</label>
                  <input
                    type="text"
                    placeholder="Nom ou email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-white/60 mb-2">Statut</label>
                  <select
                    value={userStatusFilter}
                    onChange={(e) => setUserStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  >
                    <option value="all">Tous</option>
                    <option value="ADMIN">Admin</option>
                    <option value="USER">Utilisateur</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-white/60 mb-2">Premium</label>
                  <select
                    value={userPremiumFilter}
                    onChange={(e) => setUserPremiumFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  >
                    <option value="all">Tous</option>
                    <option value="premium">Premium</option>
                    <option value="free">Gratuit</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-white/60">
                  <span className="text-white/80 font-medium">{filteredUsers.length}</span>{' '}
                  utilisateur{filteredUsers.length > 1 ? 's' : ''}.
                </div>

                <button
                  onClick={() => {
                    setUserSearchQuery('');
                    setUserStatusFilter('all');
                    setUserPremiumFilter('all');
                  }}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 bg-white/5 border border-white/10 rounded-2xl backdrop-blur overflow-hidden">
              <div className="px-4 sm:px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="text-sm text-white/70">
                  Comptes {loading && <span className="ml-2 text-white/40">• Chargement…</span>}
                </div>
              </div>

              {usersError && (
                <div className="mx-4 mt-4 bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-200 text-sm">
                  {usersError}
                </div>
              )}

              {!loading && filteredUsers.length === 0 && !usersError ? (
                <div className="py-14 text-center">
                  <div className="mx-auto w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Users className="text-white/60" />
                  </div>
                  <p className="mt-4 text-white/70 font-medium">Aucun utilisateur trouvé</p>
                  <p className="text-white/50 text-sm mt-1">Modifie les filtres pour voir plus de comptes.</p>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block h-full">
                    <div className="h-full overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-black/40 backdrop-blur border-b border-white/10">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Utilisateur</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Email</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Statut</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Premium</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Pays</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Créé le</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Dernière connexion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map((u) => {
                            const avatar = u.profile_image
                              ? (u.profile_image.startsWith('http')
                                ? u.profile_image
                                : `${BACKEND_URL}${u.profile_image}`)
                              : '/default-avatar.svg';
                            const isPremium = Boolean(u.is_premium) || String(u.subscription_type || '').toLowerCase() === 'premium';

                            return (
                              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.06] transition-colors">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={avatar}
                                      alt={u.name || u.email}
                                      className="w-10 h-10 rounded-full object-cover border border-white/10"
                                    />
                                    <div className="min-w-0">
                                      <div className="font-medium truncate">{u.name || '—'}</div>
                                      <div className="text-xs text-white/50">ID #{u.id}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-white/70">{u.email || '—'}</td>
                                <td className="px-4 py-3">
                                  <span className={cx(
                                    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border',
                                    String(u.status || '').toUpperCase() === 'ADMIN'
                                      ? 'bg-purple-500/15 text-purple-300 border-purple-500/25'
                                      : 'bg-white/10 text-white/70 border-white/15'
                                  )}>
                                    {String(u.status || 'USER').toUpperCase() === 'ADMIN' ? 'Admin' : 'Utilisateur'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  {isPremium ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/15 text-yellow-300 border border-yellow-500/25 rounded-full text-xs font-medium">
                                      <Crown size={12} />
                                      Premium
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 rounded-full text-xs font-medium">
                                      Gratuit
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-white/70">{u.country || '—'}</td>
                                <td className="px-4 py-3 text-white/70">{formatShortDate(u.created_at)}</td>
                                <td className="px-4 py-3 text-white/70">
                                  {u.last_login ? formatShortDate(u.last_login) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-white/10">
                    {filteredUsers.map((u) => {
                      const avatar = u.profile_image
                        ? (u.profile_image.startsWith('http')
                          ? u.profile_image
                          : `${BACKEND_URL}${u.profile_image}`)
                        : '/default-avatar.svg';
                      const isPremium = Boolean(u.is_premium) || String(u.subscription_type || '').toLowerCase() === 'premium';

                      return (
                        <div key={u.id} className="p-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={avatar}
                              alt={u.name || u.email}
                              className="w-10 h-10 rounded-full object-cover border border-white/10"
                            />
                            <div className="min-w-0">
                              <div className="font-medium truncate">{u.name || '—'}</div>
                              <div className="text-xs text-white/50">{u.email || '—'}</div>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/70">
                            <span className="px-2 py-1 rounded-full bg-white/10 border border-white/15">
                              {String(u.status || 'USER').toUpperCase() === 'ADMIN' ? 'Admin' : 'Utilisateur'}
                            </span>
                            {isPremium ? (
                              <span className="px-2 py-1 rounded-full bg-yellow-500/15 text-yellow-200 border border-yellow-500/25">
                                Premium
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/25">
                                Gratuit
                              </span>
                            )}
                            {u.country ? (
                              <span className="px-2 py-1 rounded-full bg-white/10 border border-white/15">
                                {u.country}
                              </span>
                            ) : null}
                            <span className="px-2 py-1 rounded-full bg-white/10 border border-white/15">
                              Créé: {formatShortDate(u.created_at)}
                            </span>
                            <span className="px-2 py-1 rounded-full bg-white/10 border border-white/15">
                              Connexion: {u.last_login ? formatShortDate(u.last_login) : '—'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* TRACKS */}
        {activeTab === 'tracks' && (
          <section className="h-full flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Gestion des Pistes</h2>
                <p className="text-sm text-white/60 mt-1">Uploade, filtre et administre rapidement ton catalogue.</p>
              </div>

              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-2.5 rounded-full font-semibold transition-colors shadow-sm"
              >
                <Plus size={18} />
                Ajouter une piste
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-6">
                  <label className="block text-xs font-medium text-white/60 mb-2">Recherche</label>
                  <input
                    type="text"
                    placeholder="Rechercher par titre / artiste…"
                    value={trackSearchQuery}
                    onChange={(e) => setTrackSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-white/60 mb-2">Type</label>
                  <select
                    value={trackTypeFilter}
                    onChange={(e) => setTrackTypeFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  >
                    <option value="all">Tout</option>
                    <option value="music">Musique</option>
                    <option value="mood">Mood</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-white/60 mb-2">Catégorie</label>
                  <select
                    value={trackCategoryFilter}
                    onChange={(e) => setTrackCategoryFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                  >
                    <option value="">Toutes</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-white/60">
                  <span className="text-white/80 font-medium">{filteredTracks.length}</span>{' '}
                  résultat{filteredTracks.length > 1 ? 's' : ''}.
                </div>

                <button
                  onClick={() => {
                    setTrackSearchQuery('');
                    setTrackCategoryFilter('');
                    setTrackTypeFilter('all');
                  }}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>

            {/* Table container */}
            <div className="flex-1 min-h-0 bg-white/5 border border-white/10 rounded-2xl backdrop-blur overflow-hidden">
              <div className="px-4 sm:px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="text-sm text-white/70">
                  Catalogue {loading && <span className="ml-2 text-white/40">• Chargement…</span>}
                </div>
              </div>

              {!loading && filteredTracks.length === 0 ? (
                <div className="py-14 text-center">
                  <div className="mx-auto w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Music className="text-white/60" />
                  </div>
                  <p className="mt-4 text-white/70 font-medium">Aucune piste trouvée</p>
                  <p className="text-white/50 text-sm mt-1">Essaie un autre filtre ou ajoute une piste.</p>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block h-full">
                    <div className="h-full overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-black/40 backdrop-blur border-b border-white/10">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Couverture</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Titre</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Artiste</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Catégorie</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Type</th>
                            <th className="text-left px-4 py-3 font-semibold text-white/70">Durée</th>
                            <th className="text-right px-4 py-3 font-semibold text-white/70">Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredTracks.map((track) => {
                            const coverSrc = track.cover_image
                              ? track.cover_image.startsWith('http')
                                ? track.cover_image
                                : `${BACKEND_URL}${track.cover_image}`
                              : null;

                            return (
                              <tr
                                key={track.id}
                                className="border-b border-white/5 hover:bg-white/[0.06] transition-colors"
                              >
                                <td className="px-4 py-3">
                                  {coverSrc ? (
                                    <img
                                      loading="lazy"
                                      decoding="async"
                                      src={coverSrc}
                                      alt={track.title}
                                      className="w-12 h-12 rounded-xl object-cover border border-white/10"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600/60 to-blue-600/60 border border-white/10 flex items-center justify-center text-lg">
                                      {track.cover_emoji || '🎵'}
                                    </div>
                                  )}
                                </td>

                                <td className="px-4 py-3">
                                  <div className="font-medium">{track.title}</div>
                                </td>

                                <td className="px-4 py-3 text-white/70">
                                  {getArtistName(track.artist_id) || track.artist || '—'}
                                </td>

                                <td className="px-4 py-3 text-white/70">
                                  {track.is_mood ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/15 text-purple-300 border border-purple-500/20 rounded-full text-xs">
                                      <Tag size={12} />
                                      Mood
                                    </span>
                                  ) : (
                                    track.category_name || 'Non catégorisé'
                                  )}
                                </td>

                                <td className="px-4 py-3">
                                  {track.is_premium ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/15 text-yellow-300 border border-yellow-500/25 rounded-full text-xs font-medium">
                                      <Crown size={12} />
                                      Premium
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 rounded-full text-xs font-medium">
                                      Gratuit
                                    </span>
                                  )}
                                </td>

                                <td className="px-4 py-3 text-white/70">{formatDuration(track.duration)}</td>

                                <td className="px-4 py-3 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      onClick={() => {
                                        setEditingTrack(track);
                                        setShowUploadModal(true);
                                      }}
                                      className="p-2 rounded-xl hover:bg-white/10 text-blue-300 hover:text-blue-200 transition-colors"
                                      title="Modifier"
                                    >
                                      <Edit2 size={18} />
                                    </button>

                                    <button
                                      onClick={() => handleDeleteTrack(track.id)}
                                      className="p-2 rounded-xl hover:bg-white/10 text-red-300 hover:text-red-200 transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 size={18} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                          <tr>
                            <td colSpan={7} className="h-8" />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden h-full overflow-auto divide-y divide-white/10">
                    {filteredTracks.map((track) => {
                      const coverSrc = track.cover_image
                        ? track.cover_image.startsWith('http')
                          ? track.cover_image
                          : `${BACKEND_URL}${track.cover_image}`
                        : null;

                      return (
                        <div key={track.id} className="p-4">
                          <div className="flex gap-3">
                            {coverSrc ? (
                              <img
                                loading="lazy"
                                decoding="async"
                                src={coverSrc}
                                alt={track.title}
                                className="w-14 h-14 rounded-2xl object-cover border border-white/10"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600/60 to-blue-600/60 border border-white/10 flex items-center justify-center text-xl">
                                {track.cover_emoji || '🎵'}
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="font-semibold truncate">{track.title}</p>
                                  <p className="text-sm text-white/60 truncate">
                                    {getArtistName(track.artist_id) || track.artist || '—'}
                                  </p>
                                </div>

                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setEditingTrack(track);
                                      setShowUploadModal(true);
                                    }}
                                    className="p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-blue-300"
                                    title="Modifier"
                                  >
                                    <Edit2 size={16} />
                                  </button>

                                  <button
                                    onClick={() => handleDeleteTrack(track.id)}
                                    className="p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-red-300"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {track.is_mood ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/15 text-purple-300 border border-purple-500/20 rounded-full text-xs">
                                    <Tag size={12} />
                                    Mood
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 bg-white/5 text-white/70 border border-white/10 rounded-full text-xs">
                                    {track.category_name || 'Non catégorisé'}
                                  </span>
                                )}

                                {track.is_premium ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/15 text-yellow-300 border border-yellow-500/25 rounded-full text-xs font-medium">
                                    <Crown size={12} />
                                    Premium
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 rounded-full text-xs font-medium">
                                    Gratuit
                                  </span>
                                )}

                                <span className="inline-flex items-center px-2 py-1 bg-white/5 text-white/70 border border-white/10 rounded-full text-xs">
                                  {formatDuration(track.duration)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="h-8" />
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {activeTab === 'artists' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <ArtistManager artists={artists} onRefresh={loadData} />
          </section>
        )}

        {activeTab === 'categories' && (
          <section className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Gestion des Catégories</h2>
                <p className="text-sm text-white/60 mt-1">Crée et organise tes univers.</p>
              </div>

              <button
                onClick={() => {
                  setEditingCategory(null);
                  setShowCategoryModal(true);
                }}
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-2.5 rounded-full font-semibold transition-colors shadow-sm"
              >
                <Plus size={18} />
                Ajouter
              </button>
            </div>

            {loading ? (
              <div className="text-white/60">Chargement...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur hover:bg-white/[0.07] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-lg truncate">{cat.name}</h3>
                        <p className="text-sm text-white/60 mt-1 line-clamp-3">{cat.description || '—'}</p>
                      </div>

                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingCategory(cat);
                            setShowCategoryModal(true);
                          }}
                          className="p-2 rounded-xl hover:bg-white/10 text-blue-300 hover:text-blue-200 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>

                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="p-2 rounded-xl hover:bg-white/10 text-red-300 hover:text-red-200 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'mood-categories' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <MoodCategoryManager />
          </section>
        )}

        {activeTab === 'playlists' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <AdminPlaylistManager />
          </section>
        )}

        {activeTab === 'sections' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <SectionManager />
          </section>
        )}

        {activeTab === 'ads' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <AdManager />
          </section>
        )}

        {activeTab === 'stories' && (
          <section className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 backdrop-blur">
            <AdminStoryManager />
          </section>
        )}
      </main>

      {/* Upload Modal */}
      <UploadTrackModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setEditingTrack(null);
        }}
        onSuccess={() => {
          setShowUploadModal(false);
          setEditingTrack(null);
          loadData();
        }}
        categories={categories}
        artists={artists}
        loading={loading}
        editingTrack={editingTrack}
        onUpload={handleUploadTrack}
      />

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 border border-white/10 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">
              {editingCategory ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
            </h3>

            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-white/80">Nom *</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingCategory?.name}
                  required
                  className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-white/80">Description</label>
                <textarea
                  name="description"
                  defaultValue={editingCategory?.description}
                  rows={4}
                  className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400/30"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full transition-colors"
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Enregistrement...' : editingCategory ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


function AnimatedCounter({ value, duration = 800 }) {
  const [display, setDisplay] = React.useState(0);

  React.useEffect(() => {
    let start = 0;
    const end = Number(value) || 0;
    if (start === end) return;

    const incrementTime = Math.max(10, Math.floor(duration / end));
    const timer = setInterval(() => {
      start += 1;
      setDisplay(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <>{display}</>;
}
